import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Button, message } from 'antd';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { Icon, Loading } from '@icp/components';
import { useTranslation } from 'react-i18next';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { ConditionalPropertyPropType } from '../../propTypes';
import FieldTitle from '../../FieldTitle';
import FormHelperText from '../../FormHelperText';
import getLocation from './getLocation';

const LocationElement = forwardRef(function LocationElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    title,
    style,
    value,
    disabled,
    readonly,
    componentProps = {},
    fieldTitleProps,
    validation,
    helpers,
    onChange,
    onTouchChanged,
  } = props;

  const {
    buttonText,
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
    reverseGeocodeUrl,
  } = componentProps;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();
  const { t } = useTranslation('icp-form-renderer');
  const className = useClassName(classNameProp);

  // value 在后端存为 JSON 字符串，组件内部使用对象
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }, [value]);

  const handleChange = useCallback(
    (locationData) => {
      onChange(JSON.stringify(locationData));
    },
    [onChange],
  );

  const [loading, setLoading] = useState(false);
  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const handleGetLocation = useCallback(() => {
    if (isInDesign) return;

    setLoading(true);

    getLocation({
      enableHighAccuracy,
      timeout,
      maximumAge,
      reverseGeocodeUrl,
    })
      .then((result) => {
        handleChange(result);
        if (onTouchChanged) {
          onTouchChanged();
        }
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    isInDesign,
    enableHighAccuracy,
    timeout,
    maximumAge,
    reverseGeocodeUrl,
    handleChange,
    onTouchChanged,
  ]);

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'location-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <div>
          {readonly ? (
            <span className="readonly-text">
              {parsedValue?.address ||
                (parsedValue ? `${parsedValue.latitude}, ${parsedValue.longitude}` : '')}
            </span>
          ) : (
            <>
              {loading && <Loading />}
              {parsedValue?.address && (
                <div className="location-element-address-display">
                  <span className="location-element-address">
                    <Icon name="ant:environment-outlined" style={{ marginRight: 4 }} />
                    {parsedValue.address}
                  </span>
                </div>
              )}
              {parsedValue && !parsedValue.address && (
                <div className="location-element-address-display">
                  <span className="location-element-address">
                    <Icon name="ant:environment-outlined" style={{ marginRight: 4 }} />
                    {`${parsedValue.latitude}, ${parsedValue.longitude}`}
                  </span>
                </div>
              )}
              <div className="location-element-actions">
                <Button
                  type="primary"
                  size="small"
                  icon={
                    parsedValue ? (
                      <Icon name="ant:reload-outlined" size={14} />
                    ) : (
                      <Icon name="ant:environment-outlined" size={14} />
                    )
                  }
                  loading={loading}
                  onClick={handleGetLocation}
                  disabled={isInDesign || disabled}
                >
                  {parsedValue
                    ? t('location.button-text-retry')
                    : buttonText || t('location.button-text')}
                </Button>
              </div>
            </>
          )}
          <FormHelperText helpers={helpers} />
        </div>
      </div>
    </ElementDecorator>
  );
});

LocationElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  style: PropTypes.shape({}),
  value: PropTypes.string,
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 获取定位按钮的文字
     */
    buttonText: PropTypes.string,
    /**
     * 是否使用高精度定位
     * @default true
     */
    enableHighAccuracy: PropTypes.bool,
    /**
     * 定位超时时间（毫秒）
     * @default 10000
     */
    timeout: PropTypes.number,
    /**
     * 缓存最大有效时间（毫秒），0 表示不缓存
     * @default 0
     */
    maximumAge: PropTypes.number,
    /**
     * 逆地理编码接口地址。定位成功后会 POST { latitude, longitude } 到此地址，
     * 期望返回 { address: "地址字符串" } 或直接返回地址字符串。
     * 不配置则使用 mock 数据。
     */
    reverseGeocodeUrl: PropTypes.string,
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName
LocationElement.displayName = 'Location';

export default withFieldWrapper(LocationElement);
